import { Button, Group, NumberInput, Select, Stack, TextInput, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useProjectCostCodes } from "@/hooks/useCostCodes";
import type { Commitment } from "@/hooks/useCommitments";
import { formatDateForStorage } from "@/lib/date-utils";

export interface CommitmentFormData {
  costCodeId: string;
  vendorName: string;
  type: "po" | "subcontract";
  amount: number;
  description: string;
  date: string;
  status: "draft" | "issued" | "received" | "paid";
}

interface CommitmentFormProps {
  onSubmit: (data: CommitmentFormData) => void;
  onCancel?: () => void;
  initialData?: Commitment;
  loading?: boolean;
  submitText?: string;
  projectId: string;
}

export function CommitmentForm({
  onSubmit,
  onCancel,
  initialData,
  loading = false,
  submitText = "Save",
  projectId,
}: CommitmentFormProps) {
  const { data: projectCostCodesData } = useProjectCostCodes(projectId);

  const form = useForm({
    initialValues: {
      costCodeId: initialData?.costCodeId || "",
      vendorName: initialData?.vendorName || "",
      type: (initialData?.type || "po") as "po" | "subcontract",
      amount: initialData?.amount || 0,
      description: initialData?.description ?? "",
      date: initialData?.date || formatDateForStorage(new Date()),
      status: (initialData?.status || "draft") as "draft" | "issued" | "received" | "paid",
    },
    validate: {
      costCodeId: (value) => (!value ? "Cost code is required" : null),
      vendorName: (value) => (!value.trim() ? "Vendor name is required" : null),
      amount: (value) => (value <= 0 ? "Amount must be greater than 0" : null),
      date: (value) => (!value ? "Date is required" : null),
    },
  });

  const handleSubmit = form.onSubmit((values) => {
    onSubmit(values as CommitmentFormData);
  });

  // Prepare cost code options from project cost codes (defensive checks for undefined)
  const projectCostCodes = projectCostCodesData?.projectCostCodes ?? [];
  const costCodeOptions = projectCostCodes.map((pcc) => ({
    value: pcc.costCodeId,
    label: `${pcc.costCode?.code ?? "?"} - ${pcc.costCode?.name ?? "Unknown"}`,
    group: pcc.costCode?.division ?? "Uncategorized",
  }));

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <Select
          label="Type"
          placeholder="Select type"
          data={[
            { value: "po", label: "Purchase Order" },
            { value: "subcontract", label: "Subcontract" },
          ]}
          required
          {...form.getInputProps("type")}
        />

        <TextInput
          label="Vendor Name"
          placeholder="Enter vendor or subcontractor name"
          required
          {...form.getInputProps("vendorName")}
        />

        <Select
          label="Cost Code"
          placeholder="Select cost code"
          data={costCodeOptions}
          searchable
          required
          {...form.getInputProps("costCodeId")}
        />

        <NumberInput
          label="Amount"
          placeholder="0.00"
          prefix="$"
          min={0}
          decimalScale={2}
          thousandSeparator=","
          required
          {...form.getInputProps("amount")}
        />

        <TextInput
          label="Date"
          type="date"
          required
          {...form.getInputProps("date")}
        />

        <Select
          label="Status"
          placeholder="Select status"
          data={[
            { value: "draft", label: "Draft" },
            { value: "issued", label: "Issued" },
            { value: "received", label: "Received" },
            { value: "paid", label: "Paid" },
          ]}
          required
          {...form.getInputProps("status")}
        />

        <Textarea
          label="Description"
          placeholder="Enter description or notes (optional)"
          rows={3}
          {...form.getInputProps("description")}
        />

        <Group justify="flex-end" mt="md">
          {onCancel && (
            <Button variant="subtle" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
          )}
          <Button type="submit" loading={loading}>
            {submitText}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
